import {
  type ApiClient,
  type ItemActivityOutletResource,
  type OutletResource,
  OutletsController_list_v1,
} from '@repo/api-client';
import { useEffect, useState } from 'react';

import { useApiClient } from '@/api/api-client';
import { collectPages } from '@/api/collect-pages';
import { systemClock } from '@/lib/clock';

import {
  currentBusinessDate,
  defaultPeriod,
  periodProblem,
  type ReportingPeriod,
} from './reporting-period';

/**
 * The state the two item reports share: the outlet, the period and the day
 * breakdown, and the read they drive. `item-report.tsx` draws it.
 *
 * `Items` (the owner's read, with cost and margin) and `ItemSales` (the
 * manager's, with neither) are the same question asked of two pairs of routes, so
 * they ask it with the same controls. **The row type is the screen's to name.**
 * {@link useItemReport} is generic over it and never looks inside a row, so the
 * manager's screen holds `ItemTradingResource` from the operation to the cell
 * and an owner-read type cannot reach it by way of this file.
 */

/** Nothing chosen: the park read, over every outlet the caller reaches. */
export const EVERY_OUTLET = '';

/** The query both pairs of routes take. */
interface ItemReportRange {
  readonly from: string;
  readonly to: string;
  readonly byDay: boolean;
}

/**
 * The two routes behind one screen: the park read, and the read that names an
 * outlet in its path.
 *
 * Two functions rather than one with an optional outlet, because they are two
 * routes with two answer shapes: only the outlet read publishes an outlet
 * envelope, and only it can answer `404`. Declared at module scope by each
 * screen, so the effect below does not refire on every render.
 */
export interface ItemReportReads<Row> {
  readonly park: (
    client: ApiClient,
    range: ItemReportRange,
    signal: AbortSignal,
  ) => Promise<{ readonly items: readonly Row[] }>;
  readonly outlet: (
    client: ApiClient,
    outletId: string,
    range: ItemReportRange,
    signal: AbortSignal,
  ) => Promise<{ readonly items: readonly Row[]; readonly outlet: ItemActivityOutletResource }>;
}

/**
 * Every state the table can be in. One value, so two cannot coexist — and a
 * report that lands after the reader has changed the scope is discarded rather
 * than rendered under the wrong heading.
 */
type ReportState<Row> =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading' }
  | {
      readonly kind: 'loaded';
      readonly items: readonly Row[];
      /** Absent on the park read, which has no single outlet to describe. */
      readonly outlet: ItemActivityOutletResource | null;
    }
  | { readonly kind: 'failed'; readonly error: unknown };

export type ItemReport<Row> = ReturnType<typeof useItemReport<Row>>;

export function useItemReport<Row>(reads: ItemReportReads<Row>) {
  const client = useApiClient();

  const [outletId, setOutletId] = useState<string>(EVERY_OUTLET);
  const [byDay, setByDay] = useState(false);
  const [period, setPeriod] = useState<ReportingPeriod>(() => defaultPeriod(systemClock));
  const [state, setState] = useState<ReportState<Row>>({ kind: 'idle' });

  // A counter rather than a boolean, so two retries in a row are two distinct
  // values and the second one still fires.
  const [reloads, setReloads] = useState(0);

  const [outlets, setOutlets] = useState<readonly OutletResource[]>([]);

  const problem = periodProblem(period, systemClock);

  // The park's own day, in Asia/Kolkata — not the device's. Read once per render
  // rather than held in state: a screen left open across midnight should bound
  // itself at the new day.
  const today = currentBusinessDate(systemClock);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    // Read whole: the outlets are bounded by the park, which is the case
    // `docs/ui-conventions.md` gives `collectPages` for. Retired ones included —
    // this is a report over a past period, and an outlet retired since it still
    // traded during it.
    collectPages<OutletResource>((offset, limit) =>
      client.request(OutletsController_list_v1, {
        query: { offset, limit, includeRetired: true },
        signal: controller.signal,
      }),
    )
      .then((all) => {
        if (live) setOutlets(all);
      })
      .catch(() => {
        // The picker is the only casualty; the park read needs no outlet.
        if (live) setOutlets([]);
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client]);

  useEffect(() => {
    // A half-filled or refused period is not a request: the notice under the
    // pickers is already saying so, and sending it would earn a 422 for
    // something the screen can see.
    if (period.from === '' || period.to === '' || problem !== null) {
      setState({ kind: 'idle' });

      return;
    }

    const controller = new AbortController();
    let live = true;

    setState({ kind: 'loading' });

    // `byDay` is a real boolean and the query is never hand-built. The routes
    // refuse `1` and `yes` with `400 VALIDATION_FAILED` rather than reading them
    // as false, because a breakdown silently dropped is a different question
    // answered `200`.
    const range = { from: period.from, to: period.to, byDay };

    const answer =
      outletId === EVERY_OUTLET
        ? reads
            .park(client, range, controller.signal)
            .then((report) => ({ items: report.items, outlet: null }))
        : reads
            .outlet(client, outletId, range, controller.signal)
            .then((report) => ({ items: report.items, outlet: report.outlet }));

    answer
      .then((landed) => {
        if (live) setState({ kind: 'loaded', items: landed.items, outlet: landed.outlet });
      })
      .catch((error: unknown) => {
        // An abort is this screen's own doing — a filter changed, or it
        // unmounted — and is not a failure to report.
        if (live && !(error instanceof DOMException && error.name === 'AbortError')) {
          setState({ kind: 'failed', error });
        }
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, reads, outletId, byDay, period.from, period.to, problem, reloads]);

  return {
    outletId,
    setOutletId,
    byDay,
    setByDay,
    period,
    setPeriod,
    problem,
    today,
    outlets,
    state,
    retry: () => {
      setReloads((count) => count + 1);
    },
  };
}
